import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '@ui/Tooltip';
import { cx } from 'class-variance-authority';
import Image from 'next/image';
import Link from 'next/link';
import NoUser from 'public/images/noimage.png';
import React, { useEffect, useState } from 'react';

const shimmer = (w: number, h: number) => `
    <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <defs>
        <linearGradient id="g">
        <stop stop-color="#333" offset="20%" />
        <stop stop-color="#222" offset="50%" />
        <stop stop-color="#333" offset="70%" />
        </linearGradient>
    </defs>
    <rect width="${w}" height="${h}" fill="#333" />
    <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
    <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
    </svg>
`;

const toBase64 = (str: string) =>
    typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);

interface Props {
    user: { id: number; name: string; image_id?: number };
    size?: number;
    selectable?: boolean;
    selected?: boolean;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    tooltip?: React.ReactNode;
    tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
    href?: string;
}

export const UserAvatar: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    user,
    size = 1000,
    selectable,
    selected,
    onClick,
    tooltip,
    href
}) => {
    const [imageSrc, setImageSrc] = useState(
        user.image_id ? `${process.env.NEXT_PUBLIC_API_HOST}image/${user.image_id}` : NoUser
    );
    useEffect(
        () => setImageSrc(user.image_id ? `${process.env.NEXT_PUBLIC_API_HOST}image/${user.image_id}` : NoUser),
        [user.image_id]
    );

    const className = cx(
        `tw-relative tw-block tw-aspect-square tw-w-full tw-rounded-full tw-shadow-md tw-transition-shadow`,
        (selectable || href) && 'tw-cursor-pointer hover:tw-shadow-lg'
    );

    const content = (
        <>
            {selected && (
                <div className="tw-absolute tw-right-0 tw-top-0 tw-z-10 tw-h-6 tw-w-6 tw-rounded-full tw-bg-success tw-text-white">
                    <FontAwesomeIcon
                        className="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2"
                        icon={faCheck}
                    />
                </div>
            )}
            <Image
                src={imageSrc}
                width={size}
                height={size}
                sizes="300px"
                className="tw-rounded-full"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(shimmer(size, size))}`}
                layout="responsive"
                unoptimized
                alt={`${user.name} Profil Bild`}
                onError={() => setImageSrc(NoUser)}
            />
        </>
    );

    return (
        <TooltipWrapper tooltip={tooltip}>
            {href ? (
                <Link href={href} onClick={(e) => selectable && onClick && onClick(e)} className={className}>
                    {content}
                </Link>
            ) : (
                /* @ts-ignore */
                <div onClick={(e) => selectable && onClick && onClick(e)} className={className}>
                    {content}
                </div>
            )}
        </TooltipWrapper>
    );
};

const TooltipWrapper: React.FunctionComponent<
    React.PropsWithChildren<React.PropsWithChildren<{ tooltip?: React.ReactNode }>>
> = ({ children, tooltip }) => {
    if (!tooltip) return <>{children}</>;
    return (
        <Tooltip tooltip={tooltip} placement="top">
            {children}
        </Tooltip>
    );
};
